/**
 * app-plus-scoring — pure APP Plus quality/QPS engine (spec v14).
 *
 * ⚠ D13 (year-compartmentalized logic): this file is the **2026
 * implementation**, owned by `src/lib/app-plus/years/2026/`. UI code must
 * NOT import functions from here directly — resolve through
 * `logicForYear(programYear)` (src/lib/app-plus/years/registry.ts) so the
 * viewing year selects the rules. TYPE imports are fine (the interfaces are
 * the year-stable contract). A later year overrides in its own module;
 * 2026's test fixtures are FROZEN.
 *
 * No I/O, no React. All percentage values in percent units (75 = 75%),
 * matching app_program_constants.json.
 *
 * (P3 — docs/reference/app-plus/IMPLEMENTATION_PLAN.md. Spec references in
 * comments cite docs/reference/app-plus/APP_Plus_2026_Spec_v14.md.)
 */
import {
  findDecile,
  parseDecileRange,
  lookupDecile,                // shared 2dp-round + fail-low gap snap (also used by MIPS)
  isBelowDecileOne,
  calculatePointsFromDecile,   // the SAME function MIPS scores with (P1 extraction)
  type DecileBenchmark,
} from './scoring-core';
import type {
  AppMeasure,
  AppBenchmarkCell,
  AppProgramConstants,
  AdminClaimsScoringConfig,
  CahpsSsm,
} from './app-plus-data-service';

// ===== BINDING TYPES =====

/** The five entered fields per clinical measure (spec §9 — rate/DC always derived). */
export interface ClinicalMeasureEntry {
  measureId: string;
  collectionType: 'eCQM' | 'MIPS CQM' | 'Medicare CQM';
  reported: boolean;
  eligiblePopulation: number;
  performanceMet: number;
  performanceNotMet: number;
  denominatorExceptions: number;
  denominatorExclusions: number;
}

export type MeasureState =
  | 'scored'
  | 'excluded_case_minimum'   // drops from available points; does NOT arm the floor
  | 'excluded_no_benchmark'   // drops from available points; ARMS the floor
  | 'unreported'              // 0 points, STAYS in available points
  | 'dc_miss';                // 0 (large) / 3 (small), STAYS in available points

export type DisplayState = 'official' | 'projected' | 'unknowable' | 'final';

/** BINDING — the unit every stage emits and stage 4 consumes. */
export interface MeasureScoreResult {
  measureId: string;
  state: MeasureState;
  /** Achievement points 0-10 (0 unless state === 'scored' or dc_miss small-practice 3). */
  points: number;
  /** True when the measure is an eCQM meeting DC + case minimum (Complex-Org input). */
  countsForComplexOrg: boolean;
  /** Collection type the result was produced under (pathway-2 disqualification input). */
  collectionType?: string;
  displayState: DisplayState;
  detail: string;
  rate: number | null;
  dataCompletenessPct: number | null;
  caseCount: number | null;
  decile: number | null;
}

export interface AcoScoringConfig {
  isSmallPractice: boolean;             // ≤15 clinicians (effectively theoretical at ACO scale)
  track: 'BASIC_A' | 'BASIC_B' | 'BASIC_C' | 'BASIC_D' | 'BASIC_E' | 'ENHANCED';
  isFirstYearFirstAgreement: boolean;   // pathway 3 input
  cahpsAdministered: boolean;
  cahpsSamplingExceptionApproved: boolean;
  eucApproved: boolean;                 // §425.512(c) override
  savingsPoolDollars: number | null;    // null = don't project dollars
  priorYearAchievementPct: number | null; // improvement input; null = no credit
}

export type PathwayStatus = 'met' | 'not_met' | 'disqualified' | 'projected' | 'unknowable';

export interface PathwayResult {
  status: PathwayStatus;
  reason: string;
}

/** BINDING — the engine's full output. */
export interface AppQualityScore {
  availablePoints: number;              // dynamic: slots×10 − 10 per exclusion
  measurePointsSum: number;
  complexOrgBonus: number;
  complexOrgBonusCap: number;
  smallPracticeBonus: number;
  improvementScore: number;
  /** Finished §3 composite incl. bonuses, capped 100, BEFORE the (a)(7)/EUC floor. */
  rawComposite: number;
  /** After floor (if armed+triggered) / EUC. This is the QPS-side score. */
  composite: number;
  floorArmed: boolean;                  // suppression or missing benchmark present
  floorApplied: boolean;                // armed AND raw < scoreFloor AND all-reported-with-DC (or EUC)
  floorReasons: string[];
  /** The MIPS-side quality input (§7.4) — ALWAYS the raw composite (Example I). */
  mipsQualityInput: number;
  pathway1: PathwayResult;
  pathway2: PathwayResult;
  pathway3: PathwayResult;
  alternative: PathwayResult;
  meetsQps: boolean;
  /** Track max when meetsQps, else scaled/zero. Percent units. */
  effectiveSharingRatePct: number;
  projectedSharedSavings: number | null;
  /** Two-sided tracks only; null for BASIC A/B. Percent units. */
  lossRatePct: number | null;
  paymentCapPct: number;                // 10 BASIC / 20 ENHANCED (display only)
  measures: MeasureScoreResult[];
}

// ===== STAGE 1 — clinical measure =====

/**
 * Spec §3: rate = met ÷ (eligiblePopulation − exclusions − exceptions);
 * DC = (met + notMet + exceptions) ÷ eligiblePopulation; eligible cases =
 * eligiblePopulation − exclusions. Below decile 1 floors at 1.0. Topped-out /
 * 7-point-cap columns are IGNORED under the APP (§3 Gates).
 */
export function scoreClinicalMeasure(
  entry: ClinicalMeasureEntry,
  measure: AppMeasure,
  cell: AppBenchmarkCell,
  constants: AppProgramConstants
): MeasureScoreResult {
  const base = {
    measureId: entry.measureId,
    collectionType: entry.collectionType,
    rate: null as number | null,
    dataCompletenessPct: null as number | null,
    caseCount: null as number | null,
    decile: null as number | null,
    countsForComplexOrg: false,
  };

  if (!entry.reported) {
    return { ...base, state: 'unreported', points: 0, displayState: 'final',
      detail: 'Required measure not reported — scores 0 and stays in available points (§3)' };
  }

  const caseCount = entry.eligiblePopulation - entry.denominatorExclusions;
  const rateDenom = entry.eligiblePopulation - entry.denominatorExclusions - entry.denominatorExceptions;
  const rate = rateDenom > 0 ? (entry.performanceMet / rateDenom) * 100 : 0;
  const dc = entry.eligiblePopulation > 0
    ? ((entry.performanceMet + entry.performanceNotMet + entry.denominatorExceptions) /
       entry.eligiblePopulation) * 100
    : 0;
  const derived = { ...base, rate, dataCompletenessPct: dc, caseCount };

  // Gate order (§3): case minimum (exclusion) → benchmark existence (exclusion)
  // → data completeness (0/3, stays in denominator).
  if (caseCount < measure.caseMinimum) {
    return { ...derived, state: 'excluded_case_minimum', points: 0, displayState: 'final',
      detail: `${caseCount} eligible cases < ${measure.caseMinimum} minimum — excluded; available points shrink; floor NOT armed (v14 pp.67-68)` };
  }
  if (cell.kind === 'none') {
    return { ...derived, state: 'excluded_no_benchmark', points: 0, displayState: 'unknowable',
      detail: `No ${entry.collectionType} benchmark for ${entry.measureId} — excluded; ARMS the §425.512(a)(7) floor` };
  }
  if (dc < constants.dataCompletenessThreshold) {
    // Large-practice points here; scoreClinicalMeasureForAco overrides for small.
    const pts = constants.dcMissPointsLargePractice;
    return { ...derived, state: 'dc_miss', points: pts, displayState: 'final',
      detail: `Data completeness ${dc.toFixed(1)}% < ${constants.dataCompletenessThreshold}% — scores ${pts}, stays in available points` };
  }

  const isInverse = measure.isInverse;
  const row = cell.row as DecileBenchmark;
  // 2dp-round before lookup so a continuous rate can't fall between bands;
  // residual gaps snap to the WORSE band (EM review B1 — now shared in scoring-core).
  const { decile, lookupRate } = lookupDecile(rate, row, isInverse);
  let points: number;
  if (decile === null) {
    points = 1.0;   // below decile 1 → 1.0 floor (§3)
  } else {
    // REUSE: the shared MIPS decile function. APP passes no cap/floor (§3:
    // topped-out/7-point-cap columns are ignored) and truncate-mode partials
    // (spec Example H). Same formula, same code path as MIPS.
    points = calculatePointsFromDecile(
      decile, lookupRate, row, isInverse,
      /* hasScoringFloor */ false, /* scoringFloor */ undefined,
      /* hasSevenPointCap */ false,
      { partialMode: 'truncate' }
    );
  }

  return {
    ...derived, decile, state: 'scored', points,
    countsForComplexOrg: entry.collectionType === 'eCQM',
    displayState: cell.kind === 'flat' || cell.kind === 'historical' ? 'final' : 'projected',
    detail: `${rate.toFixed(2)}% → decile ${decile ?? '<1'} → ${points.toFixed(1)} pts (${cell.kind} benchmark${isInverse ? ', inverse' : ''})`,
  };
}

/** BINDING — the entry point P7 uses (binds practice size before gate logic). */
export function scoreClinicalMeasureForAco(
  entry: ClinicalMeasureEntry,
  measure: AppMeasure,
  cell: AppBenchmarkCell,
  constants: AppProgramConstants,
  isSmallPractice: boolean
): MeasureScoreResult {
  const result = scoreClinicalMeasure(entry, measure, cell, constants);
  // §414.1380(b)(1) (verified against live eCFR 2026-07-20): a data-completeness
  // miss scores 0 points — EXCEPT small practices (≤15 clinicians at the APM
  // ENTITY level, §414.1305), which get a 3-point floor. This DC split is the
  // ONLY practice-size-sensitive scoring branch that survives under the APP:
  // no-benchmark / case-min misses are EXCLUSIONS for everyone regardless of
  // size (§414.1367(c)(1)(i) overrides — see assembleAppQualityScore), and the
  // PI reweighting redistribution is uniform (§414.1367(d)(2)(ii)).
  if (result.state === 'dc_miss') {
    const pts = isSmallPractice
      ? constants.dcMissPointsSmallPractice
      : constants.dcMissPointsLargePractice;
    return { ...result, points: pts,
      detail: result.detail.replace(/scores \d+/, `scores ${pts}`) };
  }
  return result;
}

/** APP rule: any benchmarked measure floors at 1.0 below decile 1 (§3). */
// ===== STAGE 2 — CAHPS (§4) =====

export interface CahpsSsmEntry {
  ssmId: string;
  n: number;
  ratePct: number;               // raw top-box %
  computeMode: 'vendor_projected' | 'cms_official';
  /** cms_official mode may carry CMS's already-scored SSM points directly. */
  officialPoints?: number;
}

export interface CahpsScoreResult extends MeasureScoreResult {
  ssmResults: Array<{
    ssmId: string; title: string; n: number; ratePct: number;
    scored: boolean; points: number | null; decile: number | null;
    /** Decile-5 lower bound — the projected ≥40th-percentile floor (§4/§6). */
    fortiethPercentileFloor: number | null;
  }>;
  scoredSsmCount: number;
}

export function scoreCahps(
  entries: CahpsSsmEntry[],
  ssms: CahpsSsm[],
  constants: AppProgramConstants,
  administered: boolean
): CahpsScoreResult {
  const base: Omit<CahpsScoreResult, 'state' | 'points' | 'displayState' | 'detail'> = {
    measureId: '321', rate: null, dataCompletenessPct: null, caseCount: null,
    decile: null, countsForComplexOrg: false, collectionType: 'CAHPS Survey Vendor',
    ssmResults: [], scoredSsmCount: 0,
  };
  if (!administered) {
    return { ...base, state: 'unreported', points: 0, displayState: 'final',
      detail: 'CAHPS not administered — 0 points, stays in available points; may fail the QPS floor condition (§6)' };
  }

  const ssmResults = ssms.map(ssm => {
    const entry = entries.find(e => e.ssmId === ssm.ssmId);
    const floor = parseDecileRange(ssm.row.decile_5).min;
    if (!entry) {
      return { ssmId: ssm.ssmId, title: ssm.title, n: 0, ratePct: 0,
        scored: false, points: null, decile: null, fortiethPercentileFloor: floor };
    }
    if (entry.n < 20) {
      // Under 20 cases the SSM DROPS from the average — never a zero (§4).
      return { ssmId: ssm.ssmId, title: ssm.title, n: entry.n, ratePct: entry.ratePct,
        scored: false, points: null, decile: null, fortiethPercentileFloor: floor };
    }
    if (entry.computeMode === 'cms_official' && entry.officialPoints != null) {
      return { ssmId: ssm.ssmId, title: ssm.title, n: entry.n, ratePct: entry.ratePct,
        scored: true, points: entry.officialPoints, decile: null, fortiethPercentileFloor: floor };
    }
    const decile = findDecile(entry.ratePct, ssm.row, false);
    const points = decile === null
      ? (isBelowDecileOne(entry.ratePct, ssm.row, false) ? 1.0 : 10)
      : calculatePointsFromDecile(decile, entry.ratePct, ssm.row, false,
          false, undefined, false, { partialMode: 'truncate' });
    return { ssmId: ssm.ssmId, title: ssm.title, n: entry.n, ratePct: entry.ratePct,
      scored: true, points, decile, fortiethPercentileFloor: floor };
  });

  const scored = ssmResults.filter(r => r.scored && r.points != null);
  const anyOfficial = entries.some(e => e.computeMode === 'cms_official');
  if (scored.length === 0) {
    return { ...base, ssmResults, state: 'unreported', points: 0, displayState: 'final',
      detail: 'CAHPS administered but no SSM met its 20-case minimum — no scoreable data' };
  }
  const avg = scored.reduce((s, r) => s + (r.points as number), 0) / scored.length;
  return {
    ...base, ssmResults, scoredSsmCount: scored.length,
    state: 'scored', points: Math.round(avg * 10) / 10,
    displayState: anyOfficial ? 'official' : 'projected',
    detail: `${scored.length} scored SSMs, simple average ${avg.toFixed(2)} (${anyOfficial ? 'CMS official' : 'projected from unadjusted vendor rates'})`,
  };
}

// ===== STAGE 3 — admin claims 479/484 (§5) =====

export interface AdminClaimsEntry {
  measureId: '479' | '484';
  mode: 'cms_official_points' | 'prior_year_params' | 'assumed_params' | 'direct_points';
  officialPoints?: number;      // cms_official_points
  ratePct?: number;             // prior_year_params / assumed_params input rate
  assumedMedian?: number;       // assumed_params
  assumedSd?: number;           // assumed_params
  directPoints?: number;        // direct_points (e.g. "assume median → 7.5")
  /** Below-case-minimum is entered, not computed (CMS counts the cases). */
  belowCaseMinimum?: boolean;
}

export function scoreAdminClaims(
  entry: AdminClaimsEntry,
  config: AdminClaimsScoringConfig
): MeasureScoreResult {
  const params = config.measures[entry.measureId];
  const base = {
    measureId: entry.measureId, collectionType: 'CMS Administrative Claims',
    rate: entry.ratePct ?? null,
    dataCompletenessPct: null, caseCount: null, decile: null,
    countsForComplexOrg: false,
  };

  if (entry.belowCaseMinimum) {
    return { ...base, state: 'excluded_case_minimum', points: 0, displayState: 'final',
      detail: `Below the ${params.caseMinimum}-case minimum — excluded (§414.1380(b)(1)(i)(A)(2)(ii)); floor NOT armed` };
  }

  switch (entry.mode) {
    case 'cms_official_points':
      return { ...base, state: 'scored', points: entry.officialPoints ?? 0,
        displayState: 'official', detail: 'CMS-official points entered' };
    case 'direct_points':
      return { ...base, state: 'scored', points: entry.directPoints ?? config.medianAnchorPoints,
        displayState: 'projected', detail: 'Analyst-assumed points (ASSUMPTION label)' };
    case 'prior_year_params': {
      if (params.priorYearMedian == null || params.priorYearSd == null || entry.ratePct == null) {
        return { ...base, state: 'scored', points: 0, displayState: 'unknowable',
          detail: `No prior-year median/SD loaded for ${entry.measureId} (484 has no prior year — new in 2026)` };
      }
      return sdLadderScore(entry, params.priorYearMedian, params.priorYearSd, config, 'projected');
    }
    case 'assumed_params': {
      if (entry.assumedMedian == null || entry.assumedSd == null || entry.ratePct == null) {
        return { ...base, state: 'scored', points: 0, displayState: 'unknowable',
          detail: 'assumed_params mode requires ratePct + assumedMedian + assumedSd' };
      }
      return sdLadderScore(entry, entry.assumedMedian, entry.assumedSd, config, 'projected');
    }
  }
}

/**
 * SD ladder (§414.1380(b)(1)(ii)(D)(2), §5): band-entry cut-offs step off the
 * median in SD multiples; PLUS-side bands sit ABOVE the median because 479/484
 * are lower-is-better (inverse via band direction, v14 §5). The exact median
 * maps to medianAnchorPoints (7.5 = mid of the 7-band, z ∈ (+0.5, −0.5]).
 * Linear partial credit within a band; z above +2.75 floors at 1.0; z at or
 * below −1.5 is a flat 10.
 */
function sdLadderScore(
  entry: AdminClaimsEntry,
  median: number,
  sd: number,
  config: AdminClaimsScoringConfig,
  displayState: DisplayState
): MeasureScoreResult {
  const rate = entry.ratePct as number;
  const z = sd > 0 ? (rate - median) / sd : 0;
  // Band-entry cut-offs for deciles 1..10 (JSON keys "1".."10").
  const cuts = Array.from({ length: 10 }, (_, i) => config.sdMultipliers[String(i + 1)]);
  let points: number;
  let decile: number;
  if (z > cuts[0]) {
    points = 1.0; decile = 1;                       // worse than the 1-band entry → floor
  } else if (z <= cuts[9]) {
    points = 10; decile = 10;
  } else {
    decile = 1;
    points = 1.0;
    for (let d = 1; d <= 9; d++) {
      const upper = cuts[d - 1];                    // band d spans (cuts[d], cuts[d-1]]
      const lower = cuts[d];
      if (z <= upper && z > lower) {
        decile = d;
        const position = (upper - z) / (upper - lower);
        points = d + Math.min(0.9, Math.max(0, Math.round(position * 10) / 10));
        return finishLadder(entry, rate, points, decile, median, sd, displayState);
      }
    }
  }
  return finishLadder(entry, rate, points, decile, median, sd, displayState);
}

function finishLadder(
  entry: AdminClaimsEntry, rate: number, points: number, decile: number,
  median: number, sd: number, displayState: DisplayState
): MeasureScoreResult {
  return {
    measureId: entry.measureId, state: 'scored', points, decile,
    rate, dataCompletenessPct: null, caseCount: null, countsForComplexOrg: false,
    collectionType: 'CMS Administrative Claims',
    displayState,
    detail: `rate ${rate} vs median ${median} (SD ${sd}) → decile ${decile} → ${points.toFixed(1)} pts (PROJECTED — SD-ladder estimate)`,
  };
}

// ===== STAGE 4 — composite, floor, pathways, savings/loss (§3, §6) =====

export function assembleAppQualityScore(
  measures: MeasureScoreResult[],
  config: AcoScoringConfig,
  constants: AppProgramConstants,
  compositeSlots: number,
  /** measureIds of the 4 outcome measures + the "remaining" list (from app_measure_set). */
  outcomeMeasureIds: string[],
  remainingMeasureIds: string[]
): AppQualityScore {
  // §414.1367(c)(1)(i) (verified against live eCFR 2026-07-20): a submitted
  // measure with no benchmark or below the case minimum is EXCLUDED from both
  // total achievement points and total AVAILABLE points — for every entity,
  // with NO practice-size split (the traditional-MIPS 0-vs-3 branch never
  // operates under the APP; only the DC gate keeps a small-practice arm).
  const excluded = measures.filter(
    m => m.state === 'excluded_case_minimum' || m.state === 'excluded_no_benchmark'
  );
  const availablePoints = compositeSlots * 10 - excluded.length * 10;

  const measurePointsSum = measures.reduce((s, m) => s + m.points, 0);

  // Complex Organization Adjustment (§3): +1 per qualifying eCQM, capped at
  // complexOrgBonusCapPct of the DYNAMIC available points (Example I: 70 → cap 7).
  const ecqmCount = measures.filter(m => m.countsForComplexOrg).length;
  const complexOrgBonusCap = Math.floor(availablePoints * (constants.complexOrgBonusCapPct / 100));
  const complexOrgBonus = Math.min(ecqmCount * constants.complexOrgBonusPerEcqm, complexOrgBonusCap);

  // §414.1380(b)(1) measure-bonus rule (NOT the sunset 2020 final-score bonus):
  // +6 when a small practice (≤15 clinicians at the APM Entity level) submits
  // ≥1 quality measure. Dormant for typical ACOs — no SSP ACO qualified PY2025.
  const smallPracticeBonus =
    config.isSmallPractice && measures.some(m => m.state === 'scored')
      ? constants.smallPracticeBonus : 0;

  // Improvement (§3): ((cur − prior) ÷ prior) × 10, cap, never negative,
  // achievement % excludes bonuses. Computed on the pre-bonus achievement rate.
  const achievementPct = availablePoints > 0 ? (measurePointsSum / availablePoints) * 100 : 0;
  let improvementScore = 0;
  if (config.priorYearAchievementPct != null && config.priorYearAchievementPct > 0) {
    improvementScore = Math.min(
      constants.improvementScoreCap,
      Math.max(0, ((achievementPct - config.priorYearAchievementPct) /
        config.priorYearAchievementPct) * 10)
    );
  }

  const rawComposite = availablePoints > 0
    ? Math.min(100,
        ((measurePointsSum + complexOrgBonus + smallPracticeBonus + improvementScore) /
          availablePoints) * 100)
    : 0;

  // §425.512(a)(7) floor: arms ONLY on suppression or missing benchmark —
  // NEVER on a case-minimum miss (v14 pp.67-68) — and requires every required
  // measure reported with DC met. EUC (§425.512(c)) floors UNCONDITIONALLY
  // (EM review B2 — EUC exists precisely for ACOs that could NOT report;
  // max() already handles the reports-anyway case). Comparator is the
  // finished (post-bonus) score.
  const floorReasons: string[] = [];
  const missingBenchmark = measures.some(m => m.state === 'excluded_no_benchmark');
  if (missingBenchmark) floorReasons.push('required measure has no benchmark (§425.512(a)(7)(ii)(B))');
  // Measure suppression (§414.1380(b)(1)(vii)(A)) has no 2026 instance; modeled
  // as a state P7 can set if CMS suppresses a measure mid-year.
  const allReportedWithDc = measures.every(
    m => m.state !== 'unreported' && m.state !== 'dc_miss'
  ) && (config.cahpsAdministered || config.cahpsSamplingExceptionApproved);
  const floorArmed = floorReasons.length > 0;
  let composite = rawComposite;
  let floorApplied = false;
  if (config.eucApproved) {
    floorReasons.push('EUC override approved (§425.512(c))');
    if (composite < constants.eucFloor) { composite = constants.eucFloor; floorApplied = true; }
  } else if (floorArmed && allReportedWithDc && composite < constants.scoreFloor) {
    composite = constants.scoreFloor;
    floorApplied = true;
  }

  // ----- pathways (§6) -----
  const scoredById = new Map(measures.map(m => [m.measureId, m]));
  const legMet = (id: string, minPoints: number): boolean => {
    const m = scoredById.get(id);
    return !!m && m.state === 'scored' && m.points >= minPoints;
  };

  const pathway1: PathwayResult = composite >= constants.qpsThreshold
    ? { status: 'met', reason: `composite ${composite.toFixed(2)} ≥ ${constants.qpsThreshold}` }
    : { status: 'not_met', reason: `composite ${composite.toFixed(2)} < ${constants.qpsThreshold}` };

  // Pathway 2 gate (§6, FULL rule — EM review M1): ALL FIVE clinical measures
  // reported as eCQM/MIPS CQM AND all five meeting data completeness. Any
  // Medicare CQM, any unreported clinical measure, or any clinical DC miss
  // disqualifies — regardless of how the legs look.
  const clinicalIds = ['001', '236', '134', '112', '113'];
  const clinical = clinicalIds.map(id => scoredById.get(id));
  const anyMedicareCqm = clinical.some(
    m => m && (m.collectionType === 'Medicare CQM' || m.detail.includes('Medicare CQM'))
  );
  const anyClinicalNotReportedWithDc = clinical.some(
    m => !m || m.state === 'unreported' || m.state === 'dc_miss'
  );
  let pathway2: PathwayResult;
  if (anyMedicareCqm) {
    pathway2 = { status: 'disqualified', reason: 'a clinical measure uses Medicare CQM (§6 pathway 2 requires all five as eCQM/MIPS CQM)' };
  } else if (anyClinicalNotReportedWithDc) {
    pathway2 = { status: 'disqualified', reason: 'pathway 2 requires all five clinical measures reported with data completeness met (§6)' };
  } else {
    // §6 "never secured" rule: a 479/484-carried leg (displayState 'projected'
    // — SD-ladder estimates; parameters post CY2027) can support at most a
    // PROJECTED pathway-2 status, never 'met'. Only final/official carriers
    // secure a leg.
    const legSecured = (id: string, minPoints: number): boolean => {
      const mm = scoredById.get(id);
      return legMet(id, minPoints) &&
        (mm!.displayState === 'final' || mm!.displayState === 'official');
    };
    const outcomeLegSecured = outcomeMeasureIds.some(id => legSecured(id, constants.percentileLegTenthMinPoints));
    const outcomeLegAny = outcomeMeasureIds.some(id => legMet(id, constants.percentileLegTenthMinPoints));
    const remainingLegSecured =
      remainingMeasureIds.some(id => legSecured(id, constants.percentileLegFortiethMinPoints));
    const remainingLegAny =
      remainingMeasureIds.some(id => legMet(id, constants.percentileLegFortiethMinPoints)) ||
      // CAHPS counts as 4 of the remaining 7; per-SSM projection is
      // mechanism-pending (§4) — a CAHPS-carried leg is at best 'projected'.
      legMet('321', constants.percentileLegFortiethMinPoints);
    // EM review M2: 'unknowable' only when an UNKNOWABLE-display cell among the
    // leg candidates could still flip the outcome — a plain leg miss with an
    // unrelated dc_miss/exclusion elsewhere is not_met, not unknowable.
    const anyUnknowableCandidate = [...outcomeMeasureIds, ...remainingMeasureIds]
      .some(id => scoredById.get(id)?.displayState === 'unknowable');
    if (outcomeLegAny && remainingLegAny) {
      pathway2 = outcomeLegSecured && remainingLegSecured
        ? { status: 'met', reason: 'both percentile legs met on secured (final/official) measures (derived decile rule, §6)' }
        : { status: 'projected', reason: 'a leg rides only on projected (479/484 estimate) or mechanism-pending CAHPS carriers — never secured (§6)' };
    } else if (anyUnknowableCandidate) {
      pathway2 = { status: 'unknowable', reason: 'a candidate leg sits on a cell with no posted benchmark' };
    } else {
      pathway2 = { status: 'not_met', reason: 'percentile legs not met' };
    }
  }

  const pathway3: PathwayResult = config.isFirstYearFirstAgreement
    ? (allReportedWithDc
        ? { status: 'met', reason: 'first-year ACO: reported set + DC met + CAHPS administered' }
        : { status: 'not_met', reason: 'first-year ACO but reporting/DC/CAHPS incomplete' })
    : { status: 'disqualified', reason: 'not a first-year ACO' };

  const altOutcomeLeg = outcomeMeasureIds.some(id => legMet(id, constants.percentileLegTenthMinPoints));
  const altProjected = outcomeMeasureIds.some(id => {
    const m = scoredById.get(id);
    return !!m && m.state === 'scored' && m.displayState === 'projected' &&
      m.points >= constants.percentileLegTenthMinPoints;
  });
  let alternative: PathwayResult = altOutcomeLeg
    ? (altProjected && !outcomeMeasureIds.some(id =>
        legMet(id, constants.percentileLegTenthMinPoints) &&
        scoredById.get(id)!.displayState !== 'projected')
        ? { status: 'projected', reason: '10th-pct leg carried only by a projected (479/484 estimated) measure' }
        : { status: 'met', reason: '≥10th percentile on an outcome measure' })
    : { status: 'not_met', reason: 'no outcome measure at ≥10th percentile' };

  // Both-fail condition (PY2026 QPS document, "Not Meeting" section — resolved
  // H2-APP asks 2/3, 2026-07-20): an ACO that (1) reports NONE of the five
  // eCQM/MIPS CQM/Medicare CQM clinical measures AND (2) does not administer
  // CAHPS (absent the sampling exception) fails BOTH standards — CMS-computed
  // 479/484 alone are NOT "reporting quality data on the set". No shared
  // savings; ENHANCED owes maximum losses (the neither-met loss arm below).
  // NOTE: data completeness does NOT gate the alternative standard (confirmed
  // expressio unius against pathways 2/3 in the same document) — DC failures
  // degrade the composite, never eligibility.
  // (clinicalIds — the five clinical measures — is declared at the pathway-2 gate above.)
  const anyClinicalReported = clinicalIds.some(id => {
    const m = scoredById.get(id);
    return !!m && m.state !== 'unreported';
  });
  const cahpsCovered = config.cahpsAdministered || config.cahpsSamplingExceptionApproved;
  if (!anyClinicalReported && !cahpsCovered) {
    alternative = { status: 'not_met',
      reason: 'both-fail condition: no eCQM/MIPS CQM/Medicare CQM reported and CAHPS not administered — fails BOTH standards (PY2026 QPS doc); no shared savings, maximum losses on ENHANCED' };
  } else if (!anyClinicalReported && (alternative.status === 'met' || alternative.status === 'projected')) {
    // Gray zone (survives the conjunction literally): CAHPS administered but
    // zero clinical submissions, leg carried by CMS-computed measures — CMS
    // adjudication untested; flagged, never a secured state (H2-APP ask 4).
    alternative = { status: 'projected',
      reason: 'gray zone: no clinical measures submitted — leg rides on CMS-computed data with only CAHPS administration; untested by CMS adjudication, never secured (H2-APP)' };
  }

  const meetsQps = pathway1.status === 'met' || pathway2.status === 'met' || pathway3.status === 'met';
  const meetsAlt = alternative.status === 'met' || alternative.status === 'projected';

  // ----- savings / loss (§6) -----
  const trackMax = constants.sharingRatePct[config.track];
  const effectiveSharingRatePct = meetsQps
    ? trackMax
    : meetsAlt ? (composite / 100) * trackMax : 0;
  const projectedSharedSavings = config.savingsPoolDollars != null
    ? config.savingsPoolDollars * (effectiveSharingRatePct / 100)
    : null;

  const twoSided = config.track !== 'BASIC_A' && config.track !== 'BASIC_B';
  let lossRatePct: number | null = null;
  if (twoSided) {
    if (config.track === 'ENHANCED') {
      lossRatePct = (meetsQps || meetsAlt)
        ? Math.min(constants.enhancedLossCapPct,
            Math.max(constants.enhancedLossFloorPct, (1 - 0.75 * (composite / 100)) * 100))
        : constants.enhancedLossNeitherMetPct;
    } else {
      lossRatePct = constants.basicLossRatePct;   // BASIC C/D/E flat, not quality-scaled
    }
  }

  return {
    availablePoints, measurePointsSum, complexOrgBonus, complexOrgBonusCap,
    smallPracticeBonus, improvementScore,
    rawComposite: round2(rawComposite), composite: round2(composite),
    floorArmed, floorApplied, floorReasons,
    mipsQualityInput: round2(rawComposite),   // Example I: MIPS side is NEVER floored
    pathway1, pathway2, pathway3, alternative, meetsQps,
    effectiveSharingRatePct: round2(effectiveSharingRatePct),
    projectedSharedSavings,
    lossRatePct: lossRatePct == null ? null : round2(lossRatePct),
    paymentCapPct: constants.paymentCapPct[config.track === 'ENHANCED' ? 'ENHANCED' : 'BASIC'],
    measures,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
